// Runtime configuration: the same build runs everywhere, so nothing
// environment-specific is baked in at build time. The container writes
// /config.js from its environment at start (nginx/40-config-js.sh); in
// development it is public/config.js. index.html loads it before the app
// and the page reads window.__PLATEA__.

export interface RuntimeConfig {
  /** The origin of the api; "" when the api shares the page's origin (the Vite proxy, nginx). */
  apiBase: string;
  /** How the caller is identified: a fixed dev user (X-Dev-User), or Entra. */
  authMode: "dev" | "entra";
  /** local, ci, dev, staging, prod. */
  env: string;
  /** The build; "dev" locally. */
  version: string;
  /** With authMode "dev": the user sent as X-Dev-User. Loggione's DEV_USERS decides who exists. */
  devUser: string;
}

declare global {
  interface Window {
    __PLATEA__?: Partial<RuntimeConfig>;
  }
}

export const DEFAULT_DEV_USER = "mrossi";

let cached: RuntimeConfig | undefined;

/** The configuration, read once; loud when config.js is missing or incomplete. */
export function runtimeConfig(): RuntimeConfig {
  if (cached) return cached;
  const raw = typeof window === "undefined" ? undefined : window.__PLATEA__;
  if (!raw || raw.apiBase === undefined || !raw.authMode) {
    throw new Error("config.js is missing or incomplete: window.__PLATEA__ needs apiBase and authMode");
  }
  if (raw.authMode !== "dev" && raw.authMode !== "entra") {
    throw new Error(`config.js: authMode must be dev or entra, not ${String(raw.authMode)}`);
  }
  // The generated paths already begin with /api/v1: an apiBase ending in /api
  // would call /api/api/v1/… and every call would 404.
  if (/\/api\/?$/.test(raw.apiBase)) {
    throw new Error(`config.js: apiBase is the api's origin ("" for the page's own), not a path: ${raw.apiBase}`);
  }
  cached = {
    apiBase: raw.apiBase.replace(/\/$/, ""),
    authMode: raw.authMode,
    env: raw.env ?? "unknown",
    version: raw.version ?? "dev",
    devUser: raw.devUser?.trim() || DEFAULT_DEV_USER,
  };
  return cached;
}

/** For tests: forget the cached configuration. */
export function resetRuntimeConfig(): void {
  cached = undefined;
}
